import { useEffect } from 'react'
import BottomSheetContent from '../../../../../../../../components/BottomSheetComponents/BottomSheetContent'
import { PrimaryButton } from '../../../../../../../../components/execution/PrimaryButton'
import { SecondaryButton } from '../../../../../../../../components/execution/SecondaryButton'
import RenderHTML from '../../../../../../../../components/ui-components/RenderHTML'
import { EventNames } from '../../../../../../../../types/misc.types'
import {
  lightImpactVibrate,
  openPrimeLandingPage,
  trackEvent
} from '../../../../../../../../utils/appBridge'
import {
  PlansBottomSheet,
  PlansLumpsumListEventIds,
  PlansScreen
} from '../../../../../analytics/plansEvents'

type RebalanceHighExposureAllocationUpsellBottomSheetProps = {
  hasAllocationPlan: boolean
  onDismiss: () => void
  onViewAllocationPlan: () => void
  onViewRebalancePlan: () => void
}

export function RebalanceHighExposureAllocationUpsellBottomSheet({
  hasAllocationPlan,
  onDismiss,
  onViewAllocationPlan,
  onViewRebalancePlan
}: RebalanceHighExposureAllocationUpsellBottomSheetProps) {
  const primaryLabel = hasAllocationPlan ? 'View allocation plan' : 'Unlock allocation plan'

  useEffect(() => {
    trackEvent({
      eventName: EventNames.BottomSheetLoad,
      payload: {
        ScreenName: PlansScreen.LumpsumRebalance,
        eventId: PlansBottomSheet.HighExposureUpsell,
        bottomSheetName: PlansBottomSheet.HighExposureUpsell
      }
    })
  }, [])

  return (
    <BottomSheetContent
      onDismiss={onDismiss}
      logoSvg={<RebalanceHighExposureDiversificationIcon />}
      heading="High exposure detected"
      bottomSheetHeadClassName="!pt-6"
      bottomSheetHeadingClassName="!text-[22px] !text-[#232323] !leading-[130%] !font-[400] !mb-0"
      bottomSheetBodyClassName="!pt-4 !pb-0"
      bottomSheetCtas={
        <div className="flex flex-col gap-3">
          <PrimaryButton
            label={primaryLabel}
            onClick={() => {
              trackEvent({
                eventName: EventNames.UserClicked,
                payload: {
                  ScreenName: PlansScreen.LumpsumRebalance,
                  eventId: PlansLumpsumListEventIds.HighExposureUpsell,
                  buttonLabel: primaryLabel
                }
              })
              if (hasAllocationPlan) {
                onViewAllocationPlan()
                return
              }
              lightImpactVibrate()
              openPrimeLandingPage()
              onDismiss()
            }}
            primaryButtonClassName="w-full !h-[56px] !font-[500] !rounded-[12px]"
          />
          <SecondaryButton
            label="View rebalance plan"
            onClick={() => {
              trackEvent({
                eventName: EventNames.UserClicked,
                payload: {
                  ScreenName: PlansScreen.LumpsumRebalance,
                  eventId: PlansLumpsumListEventIds.HighExposureUpsell,
                  buttonLabel: 'View rebalance plan'
                }
              })
              onViewRebalancePlan()
            }}
            secondaryButtonClassName="w-full !h-[56px] !font-[500] !rounded-[12px] !bg-[#F0F4F7] !border-0"
          />
        </div>
      }
    >
      <div className="flex flex-col gap-4 text-[14px] font-[400] leading-[150%] text-[#6A6E6E]">
        <p>
          We recommend reducing your exposure in this fund to avoid concentration risk. Ideally, your
          allocation plan is the best way to diversify this fund into different categories.
        </p>
        <p>
          If you&apos;d still like to rebalance within the same category, you can check the
          recommendation.
        </p>
      </div>
    </BottomSheetContent>
  )
}

export function RebalanceHighExposureDiversificationIcon() {
  return (
    <div className="size-12 flex items-center justify-center rounded-[16px]">
      <div className="rotate-90">
        <RenderHTML innerHTMLText={REBALANCE_HIGH_EXPOSURE_DIVERSIFICATION_ICON_SVG} />
      </div>
    </div>
  )
}

const REBALANCE_HIGH_EXPOSURE_DIVERSIFICATION_ICON_SVG = `<svg width="33" height="35" viewBox="0 0 33.2598 34.5491" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M1.09375 10.1562V1.09375H10.1562" stroke="#232323" stroke-width="2.1875" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M23.1035 1.09378H32.166V10.1563" stroke="#232323" stroke-width="2.1875" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M1.09375 1.09375L16.6295 16.6295L32.1652 1.09375" stroke="#232323" stroke-width="2.1875" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M16.6289 16.625V33.4554" stroke="#232323" stroke-width="2.1875" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`
